import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Mail, Inbox, Send, Settings, RefreshCw, CheckCircle, AlertCircle,
  Clock, ChevronRight, Search, Filter, MoreVertical, User, Briefcase,
  PlusCircle, Link2, ExternalLink, Loader2, Eye, Reply, Archive,
  FileText, Calendar, CheckCheck, ArrowLeft, X, Save, Trash2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { correoAPI, contactosAPI, expedientesAPI, tareasDespachoAPI } from '../api';
import { toast } from '../hooks/use-toast';

const ESTADOS_TAREA = {
  pendiente: { nombre: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  en_proceso: { nombre: 'En Proceso', color: 'bg-blue-100 text-blue-800', icon: RefreshCw },
  respondido: { nombre: 'Respondido', color: 'bg-green-100 text-green-800', icon: Reply },
  completado: { nombre: 'Completado', color: 'bg-gray-100 text-gray-800', icon: CheckCheck },
  archivado: { nombre: 'Archivado', color: 'bg-gray-50 text-gray-500', icon: Archive }
};

const PRIORIDADES = {
  baja: { nombre: 'Baja', color: 'bg-gray-100 text-gray-600' },
  normal: { nombre: 'Normal', color: 'bg-blue-100 text-blue-700' },
  alta: { nombre: 'Alta', color: 'bg-orange-100 text-orange-700' },
  urgente: { nombre: 'Urgente', color: 'bg-red-100 text-red-700' }
};

const Correo = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('gestion_info');
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emails, setEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [loadingTareas, setLoadingTareas] = useState(false);
  
  // Dialogs
  const [emailDetailDialog, setEmailDetailDialog] = useState({ open: false, email: null });
  const [crearTareaDialog, setCrearTareaDialog] = useState({ open: false, emails: [] });
  const [tareaDetailDialog, setTareaDetailDialog] = useState({ open: false, tarea: null });
  const [responderDialog, setResponderDialog] = useState({ open: false, tarea: null });
  const [crearTareaAbogadoDialog, setCrearTareaAbogadoDialog] = useState({ open: false, emails: [] });
  const [crearActuacionExpedienteDialog, setCrearActuacionExpedienteDialog] = useState({ open: false, emails: [] });
  
  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Cargar cuentas al montar
  useEffect(() => {
    loadCuentas();
    
    // Verificar si viene de OAuth callback
    const authStatus = searchParams.get('auth');
    const cuentaId = searchParams.get('cuenta');
    if (authStatus === 'success' && cuentaId) {
      toast({ title: '¡Autorización exitosa!', description: 'La cuenta de Gmail ha sido conectada.' });
    }
  }, [searchParams]);

  const loadCuentas = async () => {
    try {
      setLoading(true);
      const res = await correoAPI.getCuentas();
      setCuentas(res.data);
      
      // Cargar emails de la primera cuenta autorizada
      const cuentaActiva = res.data.find(c => c.codigo === activeTab && c.autorizado);
      if (cuentaActiva) {
        loadEmails(cuentaActiva.id);
        loadTareas(cuentaActiva.id);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmails = async (cuentaId, query = '') => {
    try {
      setLoadingEmails(true);
      const res = await correoAPI.getEmails(cuentaId, 20, null, query || undefined);
      setEmails(res.data.emails || []);
    } catch (error) {
      console.error('Error cargando emails:', error);
      if (error.response?.status === 401) {
        toast({ title: 'Sesión expirada', description: 'Necesita reautorizar la cuenta', variant: 'destructive' });
      }
    } finally {
      setLoadingEmails(false);
    }
  };

  const loadTareas = async (cuentaId) => {
    try {
      setLoadingTareas(true);
      const res = await correoAPI.getTareas(null, cuentaId);
      setTareas(res.data);
    } catch (error) {
      console.error('Error cargando tareas:', error);
    } finally {
      setLoadingTareas(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedEmails([]);
    setEmails([]);
    setTareas([]);
    
    const cuenta = cuentas.find(c => c.codigo === tab);
    if (cuenta && cuenta.autorizado) {
      loadEmails(cuenta.id);
      loadTareas(cuenta.id);
    }
  };

  const handleAutorizar = async (cuenta) => {
    try {
      const res = await correoAPI.iniciarAutorizacion(cuenta.id);
      // Abrir URL de autorización en nueva ventana
      window.location.href = res.data.authorization_url;
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'No se pudo iniciar autorización', variant: 'destructive' });
    }
  };

  const handleRefresh = () => {
    const cuenta = cuentas.find(c => c.codigo === activeTab);
    if (cuenta) {
      loadEmails(cuenta.id, searchQuery);
      loadTareas(cuenta.id);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const cuenta = cuentas.find(c => c.codigo === activeTab);
    if (cuenta) {
      loadEmails(cuenta.id, searchQuery);
    }
  };

  const handleSelectEmail = (emailId) => {
    setSelectedEmails(prev => 
      prev.includes(emailId) 
        ? prev.filter(id => id !== emailId)
        : [...prev, emailId]
    );
  };

  const handleViewEmail = async (email) => {
    const cuenta = cuentas.find(c => c.codigo === activeTab);
    if (!cuenta) return;
    
    try {
      const res = await correoAPI.getEmail(cuenta.id, email.id);
      setEmailDetailDialog({ open: true, email: res.data });
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const handleCrearTarea = () => {
    if (selectedEmails.length === 0) {
      toast({ title: 'Selecciona al menos un email', variant: 'destructive' });
      return;
    }
    
    const emailsSeleccionados = emails.filter(e => selectedEmails.includes(e.id));
    
    // Determinar el tipo de diálogo según la pestaña activa
    if (activeTab === 'gestion_abogados') {
      setCrearTareaAbogadoDialog({ open: true, emails: emailsSeleccionados });
    } else if (activeTab === 'gestion_expedientes') {
      setCrearActuacionExpedienteDialog({ open: true, emails: emailsSeleccionados });
    } else {
      setCrearTareaDialog({ open: true, emails: emailsSeleccionados });
    }
  };

  const handleGuardarEmails = async () => {
    if (selectedEmails.length === 0 || !cuentaActual) return;
    
    try {
      setLoading(true);
      const res = await correoAPI.guardarEmails(cuentaActual.id, selectedEmails);
      toast({ title: res.data.message || 'Emails guardados' });
      setSelectedEmails([]);
      handleRefresh();
    } catch (error) {
      toast({ title: 'Error al guardar', description: error.response?.data?.detail, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleArchivarEmails = async () => {
    if (selectedEmails.length === 0 || !cuentaActual) return;
    
    try {
      setLoading(true);
      const res = await correoAPI.archivarEmails(cuentaActual.id, selectedEmails);
      toast({ title: res.data.message || 'Emails archivados' });
      setSelectedEmails([]);
      handleRefresh();
    } catch (error) {
      toast({ title: 'Error al archivar', description: error.response?.data?.detail, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleMarcarLeidos = async () => {
    if (selectedEmails.length === 0 || !cuentaActual) return;
    
    try {
      setLoading(true);
      const res = await correoAPI.marcarLeidos(cuentaActual.id, selectedEmails);
      toast({ title: res.data.message || 'Emails marcados como leídos' });
      setSelectedEmails([]);
      handleRefresh();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Funciones para acciones individuales
  const handleArchivarEmail = async (emailId) => {
    if (!cuentaActual) return;
    try {
      await correoAPI.archivarEmails(cuentaActual.id, [emailId]);
      toast({ title: 'Email archivado' });
      handleRefresh();
    } catch (error) {
      toast({ title: 'Error al archivar', variant: 'destructive' });
    }
  };

  const handleGuardarEmail = async (emailId) => {
    if (!cuentaActual) return;
    try {
      await correoAPI.guardarEmails(cuentaActual.id, [emailId]);
      toast({ title: 'Email guardado' });
    } catch (error) {
      toast({ title: 'Error al guardar', variant: 'destructive' });
    }
  };

  const handleEliminarEmail = async (emailId) => {
    if (!cuentaActual) return;
    if (!window.confirm('¿Mover este email a la papelera?')) return;
    try {
      await correoAPI.eliminarEmail(cuentaActual.id, emailId);
      toast({ title: 'Email movido a la papelera' });
      handleRefresh();
    } catch (error) {
      toast({ title: 'Error al eliminar', variant: 'destructive' });
    }
  };

  const getCuentaActual = () => cuentas.find(c => c.codigo === activeTab);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="w-6 h-6 text-blue-600" />
            Módulo de Correo
          </h1>
          <p className="text-gray-600 mt-1">
            Gestión de emails y tareas de atención al cliente
          </p>
        </div>
      </div>

      {/* Tabs principales */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full max-w-xl grid-cols-3">
          <TabsTrigger value="gestion_info" className="flex items-center gap-2">
            <Inbox className="w-4 h-4" />
            Gestión Info
          </TabsTrigger>
          <TabsTrigger value="gestion_expedientes" className="flex items-center gap-2">
            <Briefcase className="w-4 h-4" />
            Gestión Expedientes
          </TabsTrigger>
          <TabsTrigger value="gestion_abogados" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            Gestión Abogados
          </TabsTrigger>
        </TabsList>

        {/* Contenido de cada tab */}
        {['gestion_info', 'gestion_expedientes', 'gestion_abogados'].map((tabId) => {
          const cuenta = cuentas.find(c => c.codigo === tabId);
          
          return (
            <TabsContent key={tabId} value={tabId} className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : !cuenta ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <AlertCircle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
                    <p className="text-gray-600">Cuenta no configurada</p>
                    <Button className="mt-4" onClick={() => window.location.href = '/configuracion'}>
                      <Settings className="w-4 h-4 mr-2" />
                      Ir a Configuración
                    </Button>
                  </CardContent>
                </Card>
              ) : !cuenta.autorizado ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Mail className="w-16 h-16 mx-auto text-blue-500 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Conectar cuenta de Gmail</h3>
                    <p className="text-gray-600 mb-2">{cuenta.email}</p>
                    <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
                      Para acceder a los emails necesitas autorizar el acceso a la cuenta de Gmail.
                      Serás redirigido a Google para completar la autorización.
                    </p>
                    <Button size="lg" onClick={() => handleAutorizar(cuenta)}>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Autorizar Acceso a Gmail
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Panel de Emails */}
                  <Card className="lg:col-span-1">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Inbox className="w-5 h-5" />
                          Bandeja de Entrada
                        </CardTitle>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={handleRefresh}>
                            <RefreshCw className={`w-4 h-4 ${loadingEmails ? 'animate-spin' : ''}`} />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Barra de búsqueda */}
                      <form onSubmit={handleSearch} className="flex gap-2 mt-3">
                        <Input
                          placeholder="Buscar emails..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="flex-1"
                        />
                        <Button type="submit" variant="outline" size="icon">
                          <Search className="w-4 h-4" />
                        </Button>
                      </form>
                      
                      {/* Acciones */}
                      {selectedEmails.length > 0 && (
                        <div className="flex items-center gap-2 mt-3 p-2 bg-blue-50 rounded-lg flex-wrap">
                          <span className="text-sm text-blue-700 font-medium">{selectedEmails.length} seleccionados</span>
                          <div className="flex gap-1 flex-wrap">
                            <Button size="sm" onClick={handleCrearTarea}>
                              <PlusCircle className="w-4 h-4 mr-1" />
                              {activeTab === 'gestion_abogados' ? 'Crear Tarea Abogado' : 
                               activeTab === 'gestion_expedientes' ? 'Añadir a Expediente' : 
                               'Crear Tarea Atención'}
                            </Button>
                            <Button size="sm" variant="outline" onClick={handleGuardarEmails}>
                              <Save className="w-4 h-4 mr-1" />
                              Guardar
                            </Button>
                            <Button size="sm" variant="outline" onClick={handleArchivarEmails}>
                              <Archive className="w-4 h-4 mr-1" />
                              Archivar
                            </Button>
                            <Button size="sm" variant="outline" onClick={handleMarcarLeidos}>
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Marcar leído
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setSelectedEmails([])}>
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardHeader>
                    <CardContent className="p-0">
                      {loadingEmails ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                        </div>
                      ) : emails.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                          <Inbox className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p>No hay emails</p>
                        </div>
                      ) : (
                        <div className="divide-y max-h-[500px] overflow-y-auto">
                          {emails.map((email) => (
                            <div
                              key={email.id}
                              className={`p-3 hover:bg-gray-50 cursor-pointer flex items-start gap-3 group ${
                                selectedEmails.includes(email.id) ? 'bg-blue-50' : ''
                              } ${email.isUnread ? 'bg-white font-medium' : 'bg-gray-50'}`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedEmails.includes(email.id)}
                                onChange={() => handleSelectEmail(email.id)}
                                className="mt-1"
                              />
                              <div className="flex-1 min-w-0" onClick={() => handleViewEmail(email)}>
                                <div className="flex items-center justify-between">
                                  <p className={`text-sm truncate ${email.isUnread ? 'font-semibold' : ''}`}>
                                    {email.from?.split('<')[0]?.trim() || email.from}
                                  </p>
                                  <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                                    {formatDate(email.date)}
                                  </span>
                                </div>
                                <p className={`text-sm truncate ${email.isUnread ? 'text-gray-900' : 'text-gray-700'}`}>
                                  {email.subject}
                                </p>
                                <p className="text-xs text-gray-500 truncate">{email.snippet}</p>
                              </div>
                              
                              {/* Botones de acción rápida (visibles al pasar el ratón) */}
                              <div className="hidden group-hover:flex items-center gap-1 flex-shrink-0">
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="h-7 w-7 p-0"
                                  title="Archivar"
                                  onClick={(e) => { e.stopPropagation(); handleArchivarEmail(email.id); }}
                                >
                                  <Archive className="w-4 h-4 text-gray-500" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="h-7 w-7 p-0"
                                  title="Guardar"
                                  onClick={(e) => { e.stopPropagation(); handleGuardarEmail(email.id); }}
                                >
                                  <Save className="w-4 h-4 text-gray-500" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="h-7 w-7 p-0 text-red-500"
                                  title="Eliminar"
                                  onClick={(e) => { e.stopPropagation(); handleEliminarEmail(email.id); }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                              
                              {email.isUnread && (
                                <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-2 group-hover:hidden" />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Panel de Tareas */}
                  <Card className="lg:col-span-1">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          <FileText className="w-5 h-5" />
                          {tabId === 'gestion_abogados' ? 'Tareas de Abogados' :
                           tabId === 'gestion_expedientes' ? 'Actuaciones Pendientes' :
                           'Tareas de Atención al Cliente'}
                        </CardTitle>
                        <Badge variant="outline">{tareas.length}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      {loadingTareas ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                        </div>
                      ) : tareas.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                          <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p>No hay tareas pendientes</p>
                          <p className="text-sm mt-1">Selecciona emails para crear tareas</p>
                        </div>
                      ) : (
                        <div className="divide-y max-h-[500px] overflow-y-auto">
                          {tareas.map((tarea) => {
                            const estadoInfo = ESTADOS_TAREA[tarea.estado] || ESTADOS_TAREA.pendiente;
                            const prioridadInfo = PRIORIDADES[tarea.prioridad] || PRIORIDADES.normal;
                            const EstadoIcon = estadoInfo.icon;
                            
                            return (
                              <div
                                key={tarea.id}
                                className="p-3 hover:bg-gray-50 cursor-pointer"
                                onClick={() => setTareaDetailDialog({ open: true, tarea })}
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2">
                                    <EstadoIcon className="w-4 h-4 text-gray-500" />
                                    <span className="font-medium text-sm truncate">{tarea.titulo}</span>
                                  </div>
                                  <Badge className={prioridadInfo.color}>{prioridadInfo.nombre}</Badge>
                                </div>
                                <p className="text-xs text-gray-600 truncate">{tarea.email_asunto}</p>
                                <div className="flex items-center justify-between mt-2">
                                  <span className="text-xs text-gray-500">{tarea.email_remitente?.split('<')[0]?.trim()}</span>
                                  <Badge className={estadoInfo.color}>{estadoInfo.nombre}</Badge>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Dialog: Ver Email */}
      <Dialog open={emailDetailDialog.open} onOpenChange={(open) => setEmailDetailDialog({ open, email: null })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              {emailDetailDialog.email?.subject || '(Sin asunto)'}
            </DialogTitle>
          </DialogHeader>
          {emailDetailDialog.email && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-1">
                <p><strong>De:</strong> {emailDetailDialog.email.from}</p>
                <p><strong>Para:</strong> {emailDetailDialog.email.to}</p>
                <p><strong>Fecha:</strong> {formatDate(emailDetailDialog.email.date)}</p>
              </div>
              <div className="prose prose-sm max-w-none">
                <div dangerouslySetInnerHTML={{ __html: emailDetailDialog.email.body?.replace(/\n/g, '<br/>') || '' }} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDetailDialog({ open: false, email: null })}>
              Cerrar
            </Button>
            <Button onClick={() => {
              if (emailDetailDialog.email) {
                setSelectedEmails([emailDetailDialog.email.id]);
                setEmailDetailDialog({ open: false, email: null });
                handleCrearTarea();
              }
            }}>
              <PlusCircle className="w-4 h-4 mr-2" />
              Crear Tarea
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Crear Tarea */}
      <CrearTareaDialog
        isOpen={crearTareaDialog.open}
        onClose={() => setCrearTareaDialog({ open: false, emails: [] })}
        emails={crearTareaDialog.emails}
        cuentaId={getCuentaActual()?.id}
        onSuccess={() => {
          setSelectedEmails([]);
          handleRefresh();
          toast({ title: 'Tarea creada correctamente' });
        }}
      />

      {/* Dialog: Detalle Tarea */}
      <TareaDetailDialog
        isOpen={tareaDetailDialog.open}
        onClose={() => setTareaDetailDialog({ open: false, tarea: null })}
        tarea={tareaDetailDialog.tarea}
        onUpdate={handleRefresh}
        onResponder={(tarea) => {
          setTareaDetailDialog({ open: false, tarea: null });
          setResponderDialog({ open: true, tarea });
        }}
      />

      {/* Dialog: Responder */}
      <ResponderDialog
        isOpen={responderDialog.open}
        onClose={() => setResponderDialog({ open: false, tarea: null })}
        tarea={responderDialog.tarea}
        onSuccess={() => {
          handleRefresh();
          toast({ title: 'Respuesta enviada correctamente' });
        }}
      />

      {/* Dialog: Crear Tarea Abogado */}
      <CrearTareaAbogadoDialog
        isOpen={crearTareaAbogadoDialog.open}
        onClose={() => setCrearTareaAbogadoDialog({ open: false, emails: [] })}
        emails={crearTareaAbogadoDialog.emails}
        onSuccess={() => {
          setSelectedEmails([]);
          handleRefresh();
          toast({ title: 'Tarea de abogado creada correctamente' });
        }}
      />

      {/* Dialog: Crear Actuación en Expediente */}
      <CrearActuacionExpedienteDialog
        isOpen={crearActuacionExpedienteDialog.open}
        onClose={() => setCrearActuacionExpedienteDialog({ open: false, emails: [] })}
        emails={crearActuacionExpedienteDialog.emails}
        onSuccess={() => {
          setSelectedEmails([]);
          handleRefresh();
          toast({ title: 'Actuación añadida al expediente correctamente' });
        }}
      />
    </div>
  );
};

// =============================================================================
// COMPONENTES AUXILIARES
// =============================================================================

const CrearTareaDialog = ({ isOpen, onClose, emails, cuentaId, onSuccess }) => {
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [prioridad, setPrioridad] = useState('normal');
  const [expedienteId, setExpedienteId] = useState('');
  const [expedientes, setExpedientes] = useState([]);
  const [searchExp, setSearchExp] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && emails.length > 0) {
      const email = emails[0];
      setTitulo(`Consulta: ${email.subject || '(Sin asunto)'}`);
      setDescripcion(`Email recibido de ${email.from}\n\n${email.snippet || ''}`);
    }
  }, [isOpen, emails]);

  useEffect(() => {
    if (searchExp.length >= 2) {
      expedientesAPI.getAll({ search: searchExp, limit: 10 }).then(res => {
        setExpedientes(res.data);
      }).catch(() => {});
    }
  }, [searchExp]);

  const handleSave = async () => {
    if (!titulo) {
      toast({ title: 'El título es obligatorio', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      for (const email of emails) {
        await correoAPI.crearTarea(cuentaId, {
          gmail_message_id: email.id,
          gmail_thread_id: email.threadId,
          email_remitente: email.from,
          email_asunto: email.subject,
          email_fecha: email.date ? new Date(email.date).toISOString() : null,
          email_snippet: email.snippet,
          titulo: emails.length > 1 ? `${titulo} (${email.subject})` : titulo,
          descripcion,
          prioridad,
          expediente_id: expedienteId ? parseInt(expedienteId) : null
        });
      }
      onClose();
      onSuccess();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'No se pudo crear la tarea', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PlusCircle className="w-5 h-5" />
            Crear Tarea de Atención
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-blue-50 p-3 rounded-lg text-sm">
            <p className="text-blue-800">
              <strong>{emails.length}</strong> email(s) seleccionado(s)
            </p>
          </div>

          <div>
            <Label>Título de la tarea *</Label>
            <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} />
          </div>

          <div>
            <Label>Descripción</Label>
            <Textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={3} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Prioridad</Label>
              <Select value={prioridad} onValueChange={setPrioridad}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="baja">Baja</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                  <SelectItem value="urgente">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vincular a Expediente</Label>
              <Input
                placeholder="Buscar expediente..."
                value={searchExp}
                onChange={(e) => setSearchExp(e.target.value)}
              />
              {expedientes.length > 0 && searchExp && (
                <div className="absolute z-10 bg-white border rounded-lg shadow-lg mt-1 max-h-40 overflow-y-auto w-full">
                  {expedientes.map(exp => (
                    <div
                      key={exp.id}
                      className="p-2 hover:bg-gray-50 cursor-pointer text-sm"
                      onClick={() => {
                        setExpedienteId(exp.id.toString());
                        setSearchExp(exp.numero_expediente || exp.titulo);
                        setExpedientes([]);
                      }}
                    >
                      {exp.numero_expediente} - {exp.titulo}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Crear Tarea
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TareaDetailDialog = ({ isOpen, onClose, tarea, onUpdate, onResponder }) => {
  const [estado, setEstado] = useState('');
  const [notas, setNotas] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (tarea) {
      setEstado(tarea.estado);
      setNotas(tarea.notas || '');
    }
  }, [tarea]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await correoAPI.actualizarTarea(tarea.id, { estado, notas });
      toast({ title: 'Tarea actualizada' });
      onUpdate();
      onClose();
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleCrearActuacion = async () => {
    if (!tarea.expediente_id) {
      toast({ title: 'La tarea no tiene expediente asignado', variant: 'destructive' });
      return;
    }
    
    try {
      await correoAPI.crearActuacion(tarea.id);
      toast({ title: 'Actuación creada en el expediente' });
      onUpdate();
      onClose();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail, variant: 'destructive' });
    }
  };

  if (!tarea) return null;

  const estadoInfo = ESTADOS_TAREA[tarea.estado] || ESTADOS_TAREA.pendiente;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            {tarea.titulo}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Info del email original */}
          <Card>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Email original:</span>
                <Badge className={estadoInfo.color}>{estadoInfo.nombre}</Badge>
              </div>
              <p className="font-medium">{tarea.email_asunto || '(Sin asunto)'}</p>
              <p className="text-sm text-gray-600">De: {tarea.email_remitente}</p>
              {tarea.email_cuerpo && (
                <div className="bg-gray-50 p-3 rounded text-sm mt-2 max-h-40 overflow-y-auto">
                  {tarea.email_cuerpo}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Estado y prioridad */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Estado</Label>
              <Select value={estado} onValueChange={setEstado}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ESTADOS_TAREA).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Expediente vinculado</Label>
              <Input value={tarea.expediente_id ? `ID: ${tarea.expediente_id}` : 'Sin vincular'} disabled />
            </div>
          </div>

          {/* Notas */}
          <div>
            <Label>Notas internas</Label>
            <Textarea value={notas} onChange={(e) => setNotas(e.target.value)} rows={3} />
          </div>

          {/* Respuestas enviadas */}
          {tarea.respuestas?.length > 0 && (
            <div>
              <Label>Respuestas enviadas</Label>
              <div className="space-y-2 mt-2">
                {tarea.respuestas.map(r => (
                  <div key={r.id} className="bg-green-50 p-3 rounded-lg text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{r.asunto}</span>
                      <Badge className="bg-green-100 text-green-700">Enviado</Badge>
                    </div>
                    <p className="text-gray-600 mt-1">{r.cuerpo.substring(0, 100)}...</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {r.enviado_por_nombre} - {r.fecha_envio}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          <Button variant="outline" onClick={() => onResponder(tarea)}>
            <Reply className="w-4 h-4 mr-2" />
            Responder
          </Button>
          {tarea.expediente_id && (
            <Button variant="outline" onClick={handleCrearActuacion}>
              <FileText className="w-4 h-4 mr-2" />
              Crear Actuación
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ResponderDialog = ({ isOpen, onClose, tarea, onSuccess }) => {
  const [destinatario, setDestinatario] = useState('');
  const [asunto, setAsunto] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (tarea) {
      // Extraer solo el email del formato "Nombre <email>"
      let email = tarea.email_remitente;
      if (email.includes('<')) {
        email = email.split('<')[1].replace('>', '').trim();
      }
      setDestinatario(email);
      setAsunto(`Re: ${tarea.email_asunto || ''}`);
      setCuerpo('');
    }
  }, [tarea]);

  const handleSend = async () => {
    if (!destinatario || !cuerpo) {
      toast({ title: 'Completa todos los campos', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      await correoAPI.responderTarea(tarea.id, { destinatario, asunto, cuerpo });
      onClose();
      onSuccess();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'No se pudo enviar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Reply className="w-5 h-5" />
            Responder Email
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Para</Label>
            <Input value={destinatario} onChange={(e) => setDestinatario(e.target.value)} />
          </div>
          <div>
            <Label>Asunto</Label>
            <Input value={asunto} onChange={(e) => setAsunto(e.target.value)} />
          </div>
          <div>
            <Label>Mensaje</Label>
            <Textarea 
              value={cuerpo} 
              onChange={(e) => setCuerpo(e.target.value)} 
              rows={8}
              placeholder="Escribe tu respuesta..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSend} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            <Send className="w-4 h-4 mr-2" />
            Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// DIALOG: Crear Tarea de Abogado
// =============================================================================
const CrearTareaAbogadoDialog = ({ isOpen, onClose, emails, onSuccess }) => {
  const [descripcion, setDescripcion] = useState('');
  const [fechaLimite, setFechaLimite] = useState('');
  const [prioridad, setPrioridad] = useState('media');
  const [expedienteId, setExpedienteId] = useState('');
  const [expedienteNumero, setExpedienteNumero] = useState('');
  const [expedientes, setExpedientes] = useState([]);
  const [searchExp, setSearchExp] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && emails.length > 0) {
      const email = emails[0];
      setDescripcion(`Email de: ${email.from}\nAsunto: ${email.subject}\n\n${email.snippet || ''}`);
    }
  }, [isOpen, emails]);

  useEffect(() => {
    if (searchExp.length >= 2) {
      expedientesAPI.getAll({ search: searchExp, limit: 10 }).then(res => {
        setExpedientes(res.data);
      }).catch(() => {});
    } else {
      setExpedientes([]);
    }
  }, [searchExp]);

  const handleSave = async () => {
    if (!descripcion) {
      toast({ title: 'La descripción es obligatoria', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      await tareasDespachoAPI.abogados.create({
        descripcion,
        fecha_limite: fechaLimite || null,
        prioridad,
        expediente_id: expedienteId ? parseInt(expedienteId) : null,
        expediente_numero: expedienteNumero || null
      });
      
      onClose();
      onSuccess();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'No se pudo crear la tarea', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />
            Crear Tarea de Abogado
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-blue-50 p-3 rounded-lg text-sm">
            <p className="text-blue-800">
              <strong>{emails.length}</strong> email(s) seleccionado(s)
            </p>
            {emails[0] && (
              <p className="text-blue-600 truncate mt-1">
                {emails[0].subject}
              </p>
            )}
          </div>

          <div>
            <Label>Descripción de la tarea *</Label>
            <Textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={4} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Fecha límite</Label>
              <Input 
                type="date" 
                value={fechaLimite} 
                onChange={(e) => setFechaLimite(e.target.value)} 
              />
            </div>
            <div>
              <Label>Prioridad</Label>
              <Select value={prioridad} onValueChange={setPrioridad}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="baja">Baja</SelectItem>
                  <SelectItem value="media">Media</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                  <SelectItem value="urgente">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Vincular a Expediente (opcional)</Label>
            <Input
              placeholder="Buscar expediente..."
              value={searchExp}
              onChange={(e) => setSearchExp(e.target.value)}
            />
            {expedientes.length > 0 && (
              <div className="bg-white border rounded-lg shadow-lg mt-1 max-h-40 overflow-y-auto">
                {expedientes.map(exp => (
                  <div
                    key={exp.id}
                    className="p-2 hover:bg-gray-50 cursor-pointer text-sm border-b last:border-0"
                    onClick={() => {
                      setExpedienteId(exp.id.toString());
                      setExpedienteNumero(exp.numero_expediente);
                      setSearchExp(exp.numero_expediente || exp.titulo);
                      setExpedientes([]);
                    }}
                  >
                    <span className="font-medium">{exp.numero_expediente}</span>
                    <span className="text-gray-500 ml-2">{exp.titulo}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Crear Tarea Abogado
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =============================================================================
// DIALOG: Crear Actuación en Expediente
// =============================================================================
const CrearActuacionExpedienteDialog = ({ isOpen, onClose, emails, onSuccess }) => {
  const [expedienteId, setExpedienteId] = useState(null);
  const [expedienteSeleccionado, setExpedienteSeleccionado] = useState(null);
  const [expedientes, setExpedientes] = useState([]);
  const [searchExp, setSearchExp] = useState('');
  const [tipoActuacion, setTipoActuacion] = useState('Comunicación recibida');
  const [descripcion, setDescripcion] = useState('');
  const [adjuntarEmail, setAdjuntarEmail] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && emails.length > 0) {
      const email = emails[0];
      setDescripcion(`Email recibido de: ${email.from}\nAsunto: ${email.subject}\nFecha: ${email.date}\n\n${email.snippet || ''}`);
    }
  }, [isOpen, emails]);

  useEffect(() => {
    if (searchExp.length >= 2) {
      expedientesAPI.getAll({ search: searchExp, limit: 10 }).then(res => {
        setExpedientes(res.data);
      }).catch(() => {});
    } else {
      setExpedientes([]);
    }
  }, [searchExp]);

  const handleSelectExpediente = (exp) => {
    setExpedienteId(exp.id);
    setExpedienteSeleccionado(exp);
    setSearchExp('');
    setExpedientes([]);
  };

  const handleSave = async () => {
    if (!expedienteId) {
      toast({ title: 'Debe seleccionar un expediente', variant: 'destructive' });
      return;
    }
    if (!descripcion) {
      toast({ title: 'La descripción es obligatoria', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      // Crear actuación en el expediente
      await expedientesAPI.createActuacion(expedienteId, {
        tipo: tipoActuacion,
        descripcion: descripcion,
        fecha: new Date().toISOString().split('T')[0]
      });
      
      onClose();
      onSuccess();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'No se pudo crear la actuación', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-green-600" />
            Añadir Email a Expediente
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-green-50 p-3 rounded-lg text-sm">
            <p className="text-green-800">
              <strong>{emails.length}</strong> email(s) seleccionado(s)
            </p>
            {emails[0] && (
              <>
                <p className="text-green-700 font-medium mt-1">{emails[0].subject}</p>
                <p className="text-green-600 text-xs">De: {emails[0].from}</p>
              </>
            )}
          </div>

          {/* Buscador de Expediente */}
          <div>
            <Label className="text-base font-semibold">Buscar Expediente *</Label>
            <Input
              placeholder="Escriba número o título del expediente..."
              value={searchExp}
              onChange={(e) => setSearchExp(e.target.value)}
              className="mt-2"
            />
            {expedientes.length > 0 && (
              <div className="bg-white border rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                {expedientes.map(exp => (
                  <div
                    key={exp.id}
                    className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-0"
                    onClick={() => handleSelectExpediente(exp)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-blue-600">{exp.numero_expediente}</span>
                      <Badge variant="outline">{exp.estado}</Badge>
                    </div>
                    <p className="text-sm text-gray-600 truncate">{exp.titulo}</p>
                    {exp.cliente_nombre && (
                      <p className="text-xs text-gray-500">Cliente: {exp.cliente_nombre}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Expediente seleccionado */}
          {expedienteSeleccionado && (
            <Card className="border-green-200 bg-green-50">
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-green-800">{expedienteSeleccionado.numero_expediente}</p>
                    <p className="text-sm text-green-700">{expedienteSeleccionado.titulo}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setExpedienteId(null);
                      setExpedienteSeleccionado(null);
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Tipo de Actuación</Label>
              <Select value={tipoActuacion} onValueChange={setTipoActuacion}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Comunicación recibida">Comunicación recibida</SelectItem>
                  <SelectItem value="Email recibido">Email recibido</SelectItem>
                  <SelectItem value="Documentación recibida">Documentación recibida</SelectItem>
                  <SelectItem value="Consulta cliente">Consulta cliente</SelectItem>
                  <SelectItem value="Notificación">Notificación</SelectItem>
                  <SelectItem value="Otros">Otros</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                id="adjuntarEmail"
                checked={adjuntarEmail}
                onChange={(e) => setAdjuntarEmail(e.target.checked)}
                className="w-4 h-4"
              />
              <Label htmlFor="adjuntarEmail" className="cursor-pointer">
                Incluir contenido del email
              </Label>
            </div>
          </div>

          <div>
            <Label>Descripción de la actuación</Label>
            <Textarea 
              value={descripcion} 
              onChange={(e) => setDescripcion(e.target.value)} 
              rows={5}
              placeholder="Descripción de la actuación..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving || !expedienteId}>
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            <FileText className="w-4 h-4 mr-2" />
            Crear Actuación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Correo;
